<script setup lang="ts">
/**
 * Действия над отмеченными строками — макет 349-4045: статус, исполнитель,
 * приоритет и закрытие одной пачкой (DEV-2054).
 *
 * Панель на surface с бордером, primary остаётся только у счётчика: заливка
 * всей панели акцентом спорила с ДС «Тихий инструмент» — цвет только в данных.
 */
import { ChevronDown } from 'lucide-vue-next'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import InlinePicker from '@/components/InlinePicker.vue'
import { PRIORITIES } from '@/features/issues/api'

import type { ProjectColumn, ProjectMember } from '@/features/projects/api'
import type { V1Priority } from '@/api/clients/tracker-issue'

const props = defineProps<{
  count: number
  columns: ProjectColumn[]
  members: ProjectMember[]
  busy?: boolean
}>()

const emit = defineEmits<{
  move: [string]
  assign: [string]
  prioritize: [V1Priority]
  close: []
  clear: []
}>()

const { t } = useI18n()

const statusOptions = computed(() =>
  props.columns.map((column) => ({ value: column.id, label: column.name })),
)

const assigneeOptions = computed(() => [
  { value: '', label: t('issues.list.noAssignee') },
  ...props.members
    .filter((member) => member.name)
    .map((member) => ({ value: member.id, label: member.name })),
])

const priorityOptions = computed(() =>
  PRIORITIES.map((value) => ({ value, label: t(`issues.priorities.${value}`) })),
)
</script>

<template>
  <div class="bulk" role="group" :aria-label="t('issues.list.bulk.label')">
    <span class="bulk__count">{{ t('issues.list.selected', { count }) }}</span>

    <InlinePicker :options="statusOptions" model-value="" @update:model-value="emit('move', String($event))">
      <template #trigger="{ isOpen, toggle }">
        <button type="button" class="bulk__action" :disabled="busy" :aria-expanded="isOpen" @click="toggle()">
          {{ t('issues.list.bulk.status') }}
          <ChevronDown :size="14" />
        </button>
      </template>
    </InlinePicker>

    <InlinePicker :options="assigneeOptions" model-value="" @update:model-value="emit('assign', String($event))">
      <template #trigger="{ isOpen, toggle }">
        <button type="button" class="bulk__action" :disabled="busy" :aria-expanded="isOpen" @click="toggle()">
          {{ t('issues.list.bulk.assignee') }}
          <ChevronDown :size="14" />
        </button>
      </template>
    </InlinePicker>

    <InlinePicker
      :options="priorityOptions"
      model-value=""
      @update:model-value="emit('prioritize', $event as V1Priority)"
    >
      <template #trigger="{ isOpen, toggle }">
        <button type="button" class="bulk__action" :disabled="busy" :aria-expanded="isOpen" @click="toggle()">
          {{ t('issues.list.bulk.priority') }}
          <ChevronDown :size="14" />
        </button>
      </template>
    </InlinePicker>

    <button type="button" class="bulk__action bulk__action--danger" :disabled="busy" @click="emit('close')">
      {{ t('issues.list.bulk.close') }}
    </button>

    <span class="bulk__spacer" />

    <button type="button" class="bulk__clear" @click="emit('clear')">
      {{ t('issues.list.clearSelection') }}
    </button>
  </div>
</template>

<style scoped>
.bulk {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 16px;
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
}

.bulk__count {
  color: var(--color-primary);
  font-size: var(--font-size-body-sm);
  font-weight: var(--font-weight-medium);
}

.bulk__action {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}

.bulk__action--danger {
  color: var(--color-danger);
}

.bulk__action:disabled {
  opacity: 0.6;
  cursor: default;
}

.bulk__spacer {
  flex: 1;
}

.bulk__clear {
  padding: 0;
  border: none;
  background: none;
  color: var(--color-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}
</style>
